// src/services/adminService.js
// Admin operations — backed by the AfriClimate Auth API (api-master)

import { AUTH } from './config';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
});

const handleRes = async (res) => {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = json.message || json.error || `HTTP ${res.status} ${res.statusText}`;
    console.error(`[adminService] ${res.status} ${res.url || ''}:`, json);
    throw new Error(msg);
  }
  return json;
};

// ── User management ───────────────────────────────────────────────────────────

/** GET /auth/users — paginated list with optional search / role filter */
export const fetchAllUsers = async (page = 1, pageSize = 20, { q, role } = {}) => {
  const p = new URLSearchParams({ page, limit: pageSize });
  if (q)    p.set('q', q);
  if (role) p.set('role', role);
  return handleRes(
    await fetch(`${AUTH.BASE_URL}/users?${p}`, { headers: getAuthHeaders() })
  );
};

/** GET /auth/stats */
export const fetchUserStats = async () =>
  handleRes(await fetch(`${AUTH.BASE_URL}/stats`, { headers: getAuthHeaders() }));

/** GET /auth/pending-verification — students and orgs awaiting admin review */
export const fetchPendingVerifications = async (page = 1) => {
  const p = new URLSearchParams({ page, limit: 20 });
  return handleRes(
    await fetch(`${AUTH.BASE_URL}/pending-verification?${p}`, { headers: getAuthHeaders() })
  );
};

/** POST /auth/users — admin creates a user directly (pre-approved, verified) */
export const adminCreateUser = async (userData) =>
  handleRes(
    await fetch(`${AUTH.BASE_URL}/users`, {
      method:  'POST',
      headers: getAuthHeaders(),
      body:    JSON.stringify(userData),
    })
  );

/** PATCH /auth/users/:userId — update role / active / assignedDevices */
export const updateUser = async (userId, updates) =>
  handleRes(
    await fetch(`${AUTH.BASE_URL}/users/${userId}`, {
      method:  'PATCH',
      headers: getAuthHeaders(),
      body:    JSON.stringify(updates),
    })
  );

export const updateUserRole    = (userId, role)   => updateUser(userId, { role });
export const suspendUser       = (userId, reason, suspendedUntil = null) =>
  updateUser(userId, { isActive: false, suspendReason: reason, ...(suspendedUntil && { suspendedUntil }) });
export const activateUser      = (userId)         => updateUser(userId, { isActive: true });

/** DELETE /auth/users/:userId — super_admin only */
export const deleteUser = async (userId) =>
  handleRes(
    await fetch(`${AUTH.BASE_URL}/users/${userId}`, {
      method:  'DELETE',
      headers: getAuthHeaders(),
    })
  );

/** DELETE /auth/suspended-users — bulk delete every suspended account */
export const deleteAllSuspended = async () =>
  handleRes(
    await fetch(`${AUTH.BASE_URL}/suspended-users`, {
      method:  'DELETE',
      headers: getAuthHeaders(),
    })
  );

/** POST /auth/users/:userId/force-verify — super_admin manually verifies email (OTP bypass) */
export const adminForceVerify = async (userId) =>
  handleRes(
    await fetch(`${AUTH.BASE_URL}/users/${userId}/force-verify`, {
      method:  'POST',
      headers: getAuthHeaders(),
    })
  );

/** POST /auth/users/:userId/resend-otp — admin resends verification OTP */
export const adminResendOtp = async (userId) =>
  handleRes(
    await fetch(`${AUTH.BASE_URL}/users/${userId}/resend-otp`, {
      method:  'POST',
      headers: getAuthHeaders(),
    })
  );

/** POST /auth/users/:userId/approve */
export const approveUser = async (userId) =>
  handleRes(
    await fetch(`${AUTH.BASE_URL}/users/${userId}/approve`, {
      method:  'POST',
      headers: getAuthHeaders(),
    })
  );

/** POST /auth/users/:userId/reject */
export const rejectUser = async (userId, reason = '') =>
  handleRes(
    await fetch(`${AUTH.BASE_URL}/users/${userId}/reject`, {
      method:  'POST',
      headers: getAuthHeaders(),
      body:    JSON.stringify({ reason }),
    })
  );

/** POST /auth/users/:userId/devices — assign device IDs to a user */
export const assignDevicesToUser = async (userId, deviceIds) =>
  handleRes(
    await fetch(`${AUTH.BASE_URL}/users/${userId}/devices`, {
      method:  'POST',
      headers: getAuthHeaders(),
      body:    JSON.stringify({ deviceIds }),
    })
  );

// Legacy alias
export const assignStationCustodian = (stationId, userId) =>
  assignDevicesToUser(userId, [stationId]);

/** Document view URL (admin-only, serves file inline) */
export const getDocumentUrl = (userId) =>
  `${AUTH.BASE_URL}/users/${userId}/document`;

// Legacy — kept for backward-compat with any other callers
export const fetchAccessRequests = fetchPendingVerifications;
export const updateAccessRequest = (userId, action, reason) =>
  action === 'approved' ? approveUser(userId) : rejectUser(userId, reason);

// ── API Keys ───────────────────────────────────────────────────────────────────

/** GET /api-keys — admin: all keys */
export const fetchAllApiKeys = async () =>
  handleRes(await fetch(`${AUTH.BASE_URL.replace('/auth', '')}/api-keys`, { headers: getAuthHeaders() }));

/** POST /api-keys/admin/generate — generate key for a user */
export const generateApiKeyForUser = async (userId, name = '') =>
  handleRes(await fetch(`${AUTH.BASE_URL.replace('/auth', '')}/api-keys/admin/generate`, {
    method: 'POST', headers: getAuthHeaders(),
    body: JSON.stringify({ userId, name }),
  }));

/** GET /api-keys/my — list own keys */
export const fetchMyApiKeys = async () =>
  handleRes(await fetch(`${AUTH.BASE_URL.replace('/auth', '')}/api-keys/my`, { headers: getAuthHeaders() }));

/** POST /api-keys/my — generate own key */
export const generateMyApiKey = async (name = '') =>
  handleRes(await fetch(`${AUTH.BASE_URL.replace('/auth', '')}/api-keys/my`, {
    method: 'POST', headers: getAuthHeaders(),
    body: JSON.stringify({ name }),
  }));

/** DELETE /api-keys/:keyId — revoke a key */
export const revokeApiKey = async (keyId) =>
  handleRes(await fetch(`${AUTH.BASE_URL.replace('/auth', '')}/api-keys/${keyId}`, {
    method: 'DELETE', headers: getAuthHeaders(),
  }));

// ── Current user ──────────────────────────────────────────────────────────────

export const checkSuperAdminStatus = async () => {
  try {
    const json = await handleRes(
      await fetch(`${AUTH.BASE_URL}/me`, { headers: getAuthHeaders() })
    );
    const u = json.user;
    return { isSuperAdmin: u?.role === 'super_admin', userData: u };
  } catch {
    return { isSuperAdmin: false, userData: null };
  }
};

const adminService = {
  fetchAllUsers, fetchUserStats, fetchPendingVerifications,
  updateUser, updateUserRole, suspendUser, activateUser, deleteUser,
  approveUser, rejectUser,
  assignDevicesToUser, assignStationCustodian,
  fetchAccessRequests, updateAccessRequest,
  getDocumentUrl, checkSuperAdminStatus,
};
export default adminService;
